'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import ScrollReveal from '@/components/ScrollReveal';
import { SparklesIcon } from '@heroicons/react/24/solid';
import { siteConfig } from '../../../config/site';
import dynamic from 'next/dynamic';

const ChatWidget = dynamic(() => import('@/components/ChatWidget'), { ssr: false });

const mockConversation = [
  { role: 'user', text: 'My PS5 is overheating and shutting down randomly.' },
  { role: 'assistant', text: 'That\'s a common issue — usually caused by dust buildup in the vents or fan, or old thermal paste. Make sure there\'s at least 4 inches of clearance around the console. If it keeps shutting down, a professional cleaning or thermal paste service can often resolve it.' },
  { role: 'user', text: 'It\'s been happening for a few weeks. Should I bring it in?' },
  { role: 'assistant', text: 'Yes — if it\'s shutting down consistently, it\'s worth having a technician look at it. Not every issue can be confirmed without inspecting the console. You can visit our shop in Long Island City or request on-site service.' },
];

export default function AIChatPromoSection() {
  const [chatOpen, setChatOpen] = useState(false);

  return (
    <section className="section-padding" style={{ backgroundColor: 'var(--bg)' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <ScrollReveal>
            <div className="space-y-5">
              <div>
                <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: 'var(--brand)' }}>
                  AI Repair Assistant
                </p>
                <h2 className="text-display font-bold" style={{ color: 'var(--text)' }}>
                  Not Sure What's Wrong?
                </h2>
                <p className="mt-3 text-base leading-relaxed" style={{ color: 'var(--text-muted)' }}>
                  Our AI Repair Assistant gives you general first-step guidance based on your symptoms. It's not a final diagnosis — a team member always confirms the details — but it's a great place to start.
                </p>
              </div>
              <ul className="space-y-2">
                {[
                  'Describe your problem in plain language',
                  'Get first-step guidance on what might be wrong',
                  'Choose to visit the shop or request on-site service',
                ]?.map((item) => (
                  <li key={item} className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-muted)' }}>
                    <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: 'var(--brand)' }} />
                    {item}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => setChatOpen(true)}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold transition-all hover:opacity-90 active:scale-95"
                style={{ backgroundColor: 'var(--brand)', color: 'var(--bg)' }}
              >
                <SparklesIcon className="w-4 h-4" />
                Try the AI Assistant
              </button>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={150}>
            <div
              className="rounded-2xl overflow-hidden"
              style={{ backgroundColor: 'var(--surface)', border: '1px solid var(--border-color)' }}
            >
              {/* Chat header */}
              <div
                className="flex items-center gap-2.5 px-4 py-3 border-b"
                style={{ borderColor: 'var(--border-color)' }}
              >
                <div
                  className="w-7 h-7 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: 'rgba(57,255,20,0.1)', border: '1px solid rgba(57,255,20,0.2)' }}
                >
                  <SparklesIcon className="w-3.5 h-3.5" style={{ color: 'var(--brand)' }} />
                </div>
                <span className="text-sm font-semibold" style={{ color: 'var(--text)' }}>AI Repair Assistant</span>
                <span
                  className="ml-auto text-xs px-2 py-0.5 rounded-full font-medium"
                  style={{ backgroundColor: 'rgba(57,255,20,0.1)', color: 'var(--brand)', fontSize: '10px' }}
                >
                  Demo
                </span>
              </div>

              {/* Mock messages */}
              <div className="p-4 space-y-3 max-h-72 overflow-y-auto">
                {mockConversation?.map((msg, i) => (
                  <div key={i} className={`flex ${msg?.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div
                      className="max-w-[85%] px-3.5 py-2.5 rounded-xl text-sm leading-relaxed"
                      style={
                        msg?.role === 'user'
                          ? { backgroundColor: 'var(--brand)', color: 'var(--bg)' }
                          : { backgroundColor: 'var(--surface-2)', border: '1px solid var(--border-color)', color: 'var(--text)' }
                      }
                    >
                      {msg?.text}
                    </div>
                  </div>
                ))}
              </div>

              {/* Action buttons */}
              <div
                className="px-4 py-3 border-t flex gap-2"
                style={{ borderColor: 'var(--border-color)' }}
              >
                <Link
                  href="/request-service"
                  className="flex-1 text-center py-2 rounded-lg text-xs font-bold transition-all hover:opacity-90"
                  style={{ backgroundColor: 'var(--brand)', color: 'var(--bg)' }}
                >
                  Request On-Site Service
                </Link>
                <a
                  href={siteConfig?.directionsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 text-center py-2 rounded-lg text-xs font-semibold border transition-all hover:opacity-80"
                  style={{ borderColor: 'var(--border-color)', color: 'var(--text)' }}
                >
                  Visit the Shop
                </a>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </div>

      {chatOpen && <ChatWidget onClose={() => setChatOpen(false)} />}
    </section>
  );
}